import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  useTheme,
  Chip,
  Grid,
  Avatar,
  Link,
  useMediaQuery,
  IconButton,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Call as CallIcon,
  Email as EmailIcon,
  Description as DocIcon,
  Comment as CommentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  CheckCircle,
  Cancel,
  Schedule,
  Assignment,
  VideoCall,
  ArrowRightAlt,
  Link as LinkIcon,
  LocationOn,
  Computer,
  Videocam,
  ArrowBack,
  Payment,
  Receipt,
  AttachMoney,
  Edit,
  Save,
  Cancel as CancelIcon,
  Add,
  Delete,
  Preview,
  Description,
} from "@mui/icons-material";

import { url } from "../constants/url";
import { useThemeToggle } from "../context/ThemeContext";

const LeadDetails = ({ userData }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userNames, setUserNames] = useState({});
  const [quotations, setQuotations] = useState([]);
  const userId = userData._id;
  const [pdfLoading, setPdfLoading] = useState(null);
  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paymentType: "token",
    paymentMethod: "bank_transfer",
    remarks: "",
    nextPaymentDate: "",
  });

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editedLead, setEditedLead] = useState({});
  const [saving, setSaving] = useState(false);

  // State for current source and service being added
  const [currentSource, setCurrentSource] = useState({
    source: "",
    otherSource: "",
  });
  const [currentService, setCurrentService] = useState({
    service: "",
    otherService: "",
  });

  // Overdue modal state
  const [overdueModalOpen, setOverdueModalOpen] = useState(false);
  const [selectedQuotationForOverdue, setSelectedQuotationForOverdue] =
    useState(null);
  const [overdueForm, setOverdueForm] = useState({
    remarks: "",
    nextPaymentDate: "",
  });

  // Alert state
  const [alert, setAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery("(max-width:480px)");

  // Status display mapping
  const statusDisplayMap = {
    hot: "Hot",
    warm: "Warm",
    closed: "Closed",
    dead: "Dead",
    rescheduled: "Rescheduled",
    quotation_shared: "Quotation Shared",
    quotation_approved: "Quotation Approved",
    quotation_revised: "Quotation Revised",
    quotation_rejected: "Quotation Rejected",
    quotation_expired: "Quotation Expired",
    meeting_scheduled: "Meeting Scheduled",
    call_not_received: "Not Received",
    "follow-up": "Follow Up",
    send_quotation: "Send Quotation",
    received: "Received",
    switch_off: "Switch Off",
    wrong_number: "Wrong Number",
    token_received: "Token Received (Overdue)",
    partially_paid: "Partially Paid (Overdue)",
    fully_paid: "Fully Paid",
    negotiation: "Negotiation",
    approved: "Approved",
    rejected: "Rejected",
    pending: "Pending",
    sent: "Sent", // 🚨 ADDED
    overdue: "Overdue Payment",
    payment_overdue: "Payment Overdue",
  };

  // Platform display mapping
  const platformDisplayMap = {
    google_meet: "Google Meet",
    zoom: "Zoom",
    teams: "Microsoft Teams",
    offline: "In-Person Meeting",
    physical: "In-Person Meeting",
    skype: "Skype",
    webex: "WebEx",
    other: "Other Platform",
  };

  // Payment status mapping
  const paymentStatusMap = {
    pending: { label: "Pending", color: "default", bgColor: "#f3f4f6" },
    partially_paid: {
      label: "Partially Paid (Overdue)",
      color: "warning",
      bgColor: "#fef3c7",
    },
    fully_paid: { label: "Fully Paid", color: "success", bgColor: "#d1fae5" },
    overdue: { label: "Overdue Payment", color: "error", bgColor: "#fee2e2" }, // Enhanced
    token_paid: { label: "Token Paid", color: "info", bgColor: "#dbeafe" },
  };

  // Payment type mapping
  const paymentTypeMap = {
    token: "Token Amount",
    partially_paid: "Partial Payment",
    fully_paid: "Full Payment",
    overdue: "Overdue Payment",
  };

  // Payment method mapping
  const paymentMethodMap = {
    cash: "Cash",
    bank_transfer: "Bank Transfer",
    cheque: "Cheque",
    online: "Online Payment",
    card: "Card",
    system: "System",
  };

  // Lead source options
  const leadSourceOptions = [
    "CEO",
    "SocialMedia",
    "Referral",
    "Website",
    "ColdCalling",
    "EmailMarketing",
    "Other",
  ];

  // Service options
  const serviceOptions = [
    "Lead Generation",
    "Podcast",
    "Social Media Management",
    "Content Marketing",
    "SEO",
    "Web Development",
    "Other",
  ];

  const isPaymentAllowed = (quotation) => {
    const allowedStatuses = [
      "approved",
      "quotation_approved",
      "token_paid",
      "partially_paid",
      "token_received",
      "payment_overdue",
      "overdue"
    ];

    const totalAmount = quotation.finalAmount || quotation.totalAmount || 0;
    const paidAmount = quotation.totalReceived || 0;
    const remainingAmount =
      quotation.remainingAmount || totalAmount - paidAmount;

    // Check both quotation specific status and overall lead status as fallbacks
    return (
      allowedStatuses.includes(quotation.status) ||
      allowedStatuses.includes(lead.quotationStatus) ||
      allowedStatuses.includes(lead.status)
    ) && remainingAmount > 0;
  };

  // Add this helper function to check if any payments are overdue
  const hasOverduePayments = useMemo(() => {
    if (!lead?.quotations) return false;

    return lead.quotations.some((quotation) => {
      // Check quotation payment status
      if (quotation.paymentStatus === "overdue") return true;

      // Check individual payment next dates
      if (quotation.payments) {
        return quotation.payments.some(
          (payment) =>
            payment.nextPaymentDate &&
            new Date(payment.nextPaymentDate) < new Date()
        );
      }

      return false;
    });
  }, [lead]);

  useEffect(() => {
    fetchLeadDetails();
  }, [leadId]);

  // Overdue Modal Component
  const OverdueModal = ({ open, onClose, quotation, onConfirm }) => {
    const [formData, setFormData] = useState({
      remarks: "",
      nextPaymentDate: "",
    });

    // Set default next payment date (tomorrow)
    useEffect(() => {
      if (open) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setFormData({
          remarks: "",
          nextPaymentDate: tomorrow.toISOString().split("T")[0],
        });
      }
    }, [open]);

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!formData.remarks.trim() || !formData.nextPaymentDate) {
        showAlert("error", "Please fill all required fields");
        return;
      }
      onConfirm(formData);
    };

    const handleClose = () => {
      setFormData({ remarks: "", nextPaymentDate: "" });
      onClose();
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: "white",
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "error.main",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Cancel sx={{ fontSize: 24 }} />
            Mark Payment as Overdue
          </Typography>
          {quotation && (
            <Typography variant="body2" color="text.secondary">
              Quotation: {quotation.quotationNumber} | Balance: ₹
              {(quotation.remainingAmount || 0).toLocaleString()}
            </Typography>
          )}
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}
            >
              {/* Warning Alert */}
              <Alert severity="warning" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="600">
                  This will update the payment status to "Overdue" and move the
                  lead for follow-up.
                </Typography>
              </Alert>

              {/* Remarks Field */}
              <TextField
                fullWidth
                label="Remarks *"
                multiline
                rows={3}
                value={formData.remarks}
                onChange={(e) =>
                  setFormData({ ...formData, remarks: e.target.value })
                }
                placeholder="Explain why this payment is overdue and what actions are needed..."
                required
                helperText="These remarks will be saved to the lead's activity history"
              />

              {/* Next Payment Date Field */}
              <TextField
                fullWidth
                label="Next Payment Date *"
                type="date"
                value={formData.nextPaymentDate}
                onChange={(e) =>
                  setFormData({ ...formData, nextPaymentDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
                helperText="The lead will be scheduled for follow-up on this date"
                inputProps={{
                  min: new Date().toISOString().split("T")[0], // Can't select past dates
                }}
              />

              {/* Summary Card */}
              <Card variant="outlined" sx={{ bgcolor: "grey.50", p: 2 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Summary of Changes:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption">Payment Status:</Typography>
                    <Chip label="OVERDUE" color="error" size="small" />
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption">Lead Status:</Typography>
                    <Typography
                      variant="caption"
                      fontWeight="600"
                      color="error.main"
                    >
                      Payment Overdue
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="caption">Next Follow-up:</Typography>
                    <Typography variant="caption" fontWeight="600">
                      {formData.nextPaymentDate
                        ? formatDate(formData.nextPaymentDate)
                        : "Not set"}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={handleClose}
              color="inherit"
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="error"
              disabled={!formData.remarks.trim() || !formData.nextPaymentDate}
              startIcon={<Cancel />}
              sx={{ minWidth: 140 }}
            >
              Mark as Overdue
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  };

  const handleMarkAsOverdue = (quotation) => {
    console.log("🔄 Opening overdue modal for quotation:", quotation);

    if (!quotation || !lead || !userId) {
      showAlert("error", "Required data missing");
      return;
    }

    // Set the quotation and open modal
    setSelectedQuotationForOverdue(quotation);
    setOverdueModalOpen(true);
  };

  // Handle confirming the overdue status
  // const handleConfirmOverdue = async (overdueData) => {
  //   try {
  //     if (!selectedQuotationForOverdue || !lead || !userId) {
  //       showAlert('error', 'Required data missing');
  //       return;
  //     }

  //     console.log('📤 Marking payment as overdue:', {
  //       quotation: selectedQuotationForOverdue.quotationNumber,
  //       remarks: overdueData.remarks,
  //       nextPaymentDate: overdueData.nextPaymentDate
  //     });

  //     const updateData = {
  //       leadId: lead._id,
  //       quotationId: selectedQuotationForOverdue._id,
  //       paymentStatus: 'overdue',
  //       userId: userId,
  //       remarks: overdueData.remarks,
  //       nextPaymentDate: overdueData.nextPaymentDate
  //     };

  //     const response = await fetch(`${url}/api/leads/update-payment-status`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(updateData)
  //     });

  //     const data = await response.json();

  //     if (!response.ok) {
  //       throw new Error(data.message || 'Failed to update payment status');
  //     }

  //     if (!data.success) {
  //       throw new Error(data.message || 'Payment status update failed');
  //     }

  //     console.log("✅ Payment status updated successfully:", data);

  //     // Close modal and refresh data
  //     setOverdueModalOpen(false);
  //     setSelectedQuotationForOverdue(null);

  //     showAlert('success', 'Payment marked as overdue successfully!');

  //     // Refresh lead data
  //     setTimeout(() => {
  //       fetchLeadDetails();
  //     }, 500);

  //   } catch (error) {
  //     console.error('❌ Error updating payment status:', error);
  //     showAlert('error', error.message);
  //     setOverdueModalOpen(false);
  //   }
  // };
  const handleConfirmOverdue = async (overdueData) => {
    try {
      if (!selectedQuotationForOverdue || !lead || !userId) {
        showAlert("error", "Required data missing");
        return;
      }

      console.log("📤 Marking payment as overdue:", {
        quotation: selectedQuotationForOverdue.quotationNumber,
        remarks: overdueData.remarks,
        nextPaymentDate: overdueData.nextPaymentDate,
      });

      const totalAmount =
        selectedQuotationForOverdue.finalAmount ||
        selectedQuotationForOverdue.totalAmount ||
        0;
      const paidAmount = selectedQuotationForOverdue.totalReceived || 0;
      const remainingAmount =
        selectedQuotationForOverdue.remainingAmount || totalAmount - paidAmount;

      const updateData = {
        leadId: lead._id,
        quotationId: selectedQuotationForOverdue._id,
        paymentStatus: "overdue",
        userId: userId,
        remarks: overdueData.remarks,
        nextPaymentDate: overdueData.nextPaymentDate,
        // 🚨 CRITICAL: Force sync lead status
        updateLeadStatus: true,
        newLeadStatus: "payment_overdue",
      };

      console.log("📊 Payment overdue details:", {
        totalAmount,
        paidAmount,
        remainingAmount,
        updateData,
      });

      const response = await fetch(`${url}/api/leads/update-payment-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update payment status");
      }

      if (!data.success) {
        throw new Error(data.message || "Payment status update failed");
      }

      console.log("✅ Payment status updated successfully:", data);

      // Close modal and refresh data
      setOverdueModalOpen(false);
      setSelectedQuotationForOverdue(null);

      showAlert("success", "Payment marked as overdue successfully!");

      // Refresh lead data
      await fetchLeadDetails();
    } catch (error) {
      console.error("❌ Error updating payment status:", error);
      showAlert("error", error.message);
      setOverdueModalOpen(false);
    }
  };
  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${url}/api/leads/fetch-lead-details?leadId=${leadId}`
      );
      const data = await res.json();
      setLead(data || null);
      setEditedLead(data || {});

      if (data?.quotations) {
        setQuotations(data.quotations);
      }
    } catch (err) {
      console.error("Error fetching lead details", err);
      showAlert("error", "Failed to fetch lead details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserNames = async () => {
      if (!lead) return;
      const namesMap = { ...userNames };
      const userIds = new Set();

      lead.statusRecords?.forEach(
        (r) => r.updatedBy && userIds.add(r.updatedBy)
      );
      lead.remarks?.forEach((r) => r.addedBy && userIds.add(r.addedBy));
      lead.quotations?.forEach((q) => {
        q.payments?.forEach((p) => p.receivedBy && userIds.add(p.receivedBy));
      });

      for (let id of userIds) {
        if (!namesMap[id]) {
          try {
            const res = await fetch(`${url}/api/users/fetch-user?userId=${id}`);
            const data = await res.json();
            namesMap[id] = data?.name || "User (Incomplete Profile)";
          } catch {
            namesMap[id] = "Unknown User";
          }
        }
      }
      setUserNames(namesMap);
    };
    fetchUserNames();
  }, [lead]);

  // Show alert function
  const showAlert = (severity, message) => {
    setAlert({
      open: true,
      severity,
      message,
    });
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Edit functionality with multiple selection interface
  const handleEditToggle = () => {
    if (editMode) {
      // Save changes
      handleSaveChanges();
    } else {
      // Enter edit mode - initialize with current data
      setEditedLead({
        ...lead,
        leadSources: lead.leadSources || [
          lead.leadSource || { source: "", otherSource: "", details: "" },
        ],
        serviceInterested: lead.serviceInterested || [],
      });

      // Initialize current source and service for adding new ones
      setCurrentSource({ source: "", otherSource: "" });
      setCurrentService({ service: "", otherService: "" });
    }
    setEditMode(!editMode);
  };

  // Add lead source
  const addSource = () => {
    if (!currentSource.source.trim()) {
      showAlert("error", "Please select a lead source.");
      return;
    }

    const sourceValue =
      currentSource.source === "Other"
        ? currentSource.otherSource
        : currentSource.source;

    if (!sourceValue.trim()) {
      showAlert("error", "Please specify the source.");
      return;
    }

    // Check if source already exists
    if (
      editedLead.leadSources?.some((source) => source.source === sourceValue)
    ) {
      showAlert("error", "This source has already been added.");
      return;
    }

    const newSource = {
      source: sourceValue,
      originalSource: currentSource.source,
      otherSource:
        currentSource.source === "Other" ? currentSource.otherSource : null,
      details: "",
    };

    setEditedLead((prev) => ({
      ...prev,
      leadSources: [...(prev.leadSources || []), newSource],
    }));

    // Reset current source
    setCurrentSource({ source: "", otherSource: "" });
  };

  // Remove lead source
  const removeSource = (index) => {
    setEditedLead((prev) => ({
      ...prev,
      leadSources: prev.leadSources.filter((_, i) => i !== index),
    }));
  };

  // Add service
  const addService = () => {
    if (!currentService.service.trim()) {
      showAlert("error", "Please select a service.");
      return;
    }

    const serviceValue =
      currentService.service === "Other"
        ? currentService.otherService
        : currentService.service;

    if (!serviceValue.trim()) {
      showAlert("error", "Please specify the service.");
      return;
    }

    // Check if service already exists
    if (
      editedLead.serviceInterested?.some(
        (service) => service.service === serviceValue
      )
    ) {
      showAlert("error", "This service has already been added.");
      return;
    }

    const newService = {
      service: serviceValue,
      originalService: currentService.service,
      otherService:
        currentService.service === "Other" ? currentService.otherService : null,
      price: 0,
      quantity: 1,
      description: "",
    };

    setEditedLead((prev) => ({
      ...prev,
      serviceInterested: [...(prev.serviceInterested || []), newService],
    }));

    // Reset current service
    setCurrentService({ service: "", otherService: "" });
  };

  // Remove service
  const removeService = (index) => {
    setEditedLead((prev) => ({
      ...prev,
      serviceInterested: prev.serviceInterested.filter((_, i) => i !== index),
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);

      // Prepare the data for API call - FIXED: Use correct field names
      const updateData = {
        clientName: editedLead.clientName,
        contactNo: editedLead.contactNo,
        email: editedLead.email,
        leadSources: editedLead.leadSources || [], // FIXED: Use leadSources (plural)
        servicesInterested: editedLead.serviceInterested || [], // FIXED: Use servicesInterested
      };

      console.log("🔄 Saving lead data:", updateData);

      const response = await fetch(`${url}/api/leads/update-lead/${lead._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server error response:", errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      // FIXED: Properly update state and refresh
      setLead(data);
      setEditedLead(data);
      setEditMode(false);
      showAlert("success", "Lead updated successfully!");

      // FIXED: Force refresh of all data
      await fetchLeadDetails();
    } catch (error) {
      console.error("Error updating lead:", error);
      showAlert("error", `Error updating lead: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedLead(lead);
    setEditMode(false);
    setCurrentSource({ source: "", otherSource: "" });
    setCurrentService({ service: "", otherService: "" });
  };

  // Format services for display
  const formatServices = (services) => {
    if (!services || !Array.isArray(services)) return "No services";
    return services.map((s) => s.otherService || s.service).join(", ");
  };

  // Format date to "18 Nov 2025" format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Format time to "3:41 PM" format
  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Helper function to get activity color
  const getActivityIconColor = (type, title = "", status = "") => {
    const text = title.toLowerCase();
    const statusLower = status.toLowerCase();

    // Status specific colors
    if (type === "status") {
      if (statusLower.includes("hot")) return "#EF4444"; // Red
      if (statusLower.includes("warm")) return "#F97316"; // Orange
      if (statusLower.includes("closed") || statusLower.includes("won"))
        return "#22C55E"; // Green
      if (statusLower.includes("dead") || statusLower.includes("lost"))
        return "#6B7280"; // Gray
      if (statusLower.includes("quotation")) return "#FACC15"; // Yellow
      if (statusLower.includes("meeting")) return "#8B5CF6"; // Purple
      if (statusLower.includes("rescheduled")) return "#6366F1"; // Indigo
      if (statusLower.includes("follow")) return "#F59E0B"; // Amber
      return "#3B82F6"; // Default blue for status
    }

    // Type specific colors
    if (type === "quotation") return "#10B981"; // Emerald
    if (type === "payment") return "#10B981"; // Emerald
    if (type === "remark") {
      if (text.includes("call")) return "#22C55E"; // Green
      if (text.includes("email")) return "#F97316"; // Orange
      if (text.includes("meeting")) return "#8B5CF6"; // Purple
      if (text.includes("urgent") || text.includes("important"))
        return "#EF4444"; // Red
      return "#052b3dff"; // Default blue for remarks
    }

    return "#6B7280"; // Default gray
  };

  // Helper function to get activity icon
  const getActivityIcon = (type, title = "", status = "") => {
    const titleLower = title.toLowerCase();
    const statusLower = status.toLowerCase();

    if (type === "quotation") return <DocIcon />;
    if (type === "payment") return <AttachMoney />;
    if (type === "status") {
      if (statusLower.includes("won") || titleLower.includes("won"))
        return <CheckCircle />;
      if (statusLower.includes("lost") || titleLower.includes("lost"))
        return <Cancel />;
      if (
        statusLower.includes("meeting") ||
        statusLower.includes("rescheduled")
      )
        return <Schedule />;
      if (statusLower.includes("quotation")) return <Assignment />;
      if (statusLower.includes("hot") || statusLower.includes("warm"))
        return <CallIcon />;
      return <Assignment />;
    }
    if (type === "remark") {
      if (titleLower.includes("call")) return <CallIcon />;
      if (titleLower.includes("email")) return <EmailIcon />;
      if (titleLower.includes("meeting")) return <VideoCall />;
      return <CommentIcon />;
    }
    return <CommentIcon />;
  };

  // Add this function to your LeadDetails component
  const handleViewQuotationPDF = async (event) => {
    const quotationId = event.details?._id || event.quotationData?._id;

    try {
      setPdfLoading(quotationId); // Start loading

      // Use the quotation data directly from the event
      const quotation = event.details || event.quotationData;

      if (!quotation) {
        showAlert("error", "No quotation data available for this activity.");
        setPdfLoading(null);
        return;
      }

      console.log("📄 Opening PDF for quotation:", quotation.quotationNumber);

      // Call the combined PDF endpoint
      const response = await fetch(`${url}/api/templates/preview-combined`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leadId: lead._id,
          quotationId: quotation._id,
          userId: userId
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate PDF");
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Generated PDF is empty");
      }

      // Open PDF in new tab
      const pdfUrl = URL.createObjectURL(blob);
      const newWindow = window.open(pdfUrl, "_blank");

      if (!newWindow) {
        showAlert(
          "warning",
          "Popup blocked! Please allow popups to view the PDF."
        );
      }
    } catch (error) {
      console.error("❌ Error viewing quotation PDF:", error);
      showAlert("error", "Failed to open PDF: " + error.message);
    } finally {
      setPdfLoading(null); // Stop loading regardless of success/error
    }
  };

  // In generateQuotationPDF function
  const generateQuotationPDF = async (quotation, service) => {
    try {
      const templateName =
        service.serviceType || quotation.templateUsed || "CustomService";

      // Prepare template values with actual data
      const templateValues = {
        clientName: lead.clientName,
        clientEmail: lead.email,
        phone: lead.contactNo,
        date: new Date(
          quotation.sentDate || quotation.createdAt
        ).toLocaleDateString(),
        validUntil: quotation.validUntil
          ? new Date(quotation.validUntil).toLocaleDateString()
          : "",
        serviceName: service.serviceName,
        serviceTotal: service.calculatedTotal || 0,
        totalAmount: quotation.totalAmount || 0,
        discount: quotation.discount || 0,
        finalAmount: quotation.finalAmount || 0,
        quotationNumber: quotation.quotationNumber || `QT-${Date.now()}`,
      };

      // Add all service field values to template
      if (service.fields) {
        service.fields.forEach((field) => {
          templateValues[field.name] = field.value || "";
        });
      }

      console.log("📤 Generating PDF with values:", templateValues);

      const payload = {
        leadId: lead._id,
        userId: userId,
        templateName: templateName,
        values: templateValues,
      };

      const response = await fetch(`${url}/api/templates/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate PDF");
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Generated PDF is empty");
      }

      // Open PDF in new tab
      const pdfUrl = URL.createObjectURL(blob);
      const newWindow = window.open(pdfUrl, "_blank");

      if (!newWindow) {
        showAlert(
          "warning",
          "Popup blocked! Please allow popups to view the PDF."
        );
      }
    } catch (error) {
      console.error("❌ PDF generation error:", error);
      throw error; // Re-throw to be handled by the calling function
    }
  };
  // Helper function to find the quotation associated with a status record
  const findQuotationByStatusRecord = (lead, statusRecord) => {
    if (!lead.quotations || lead.quotations.length === 0) return null;

    const statusDate = new Date(statusRecord.updatedAt);

    // Find the quotation that was most likely active at this time
    // Usually it's the last quotation created before this status update
    const relevantQuotations = lead.quotations.filter(
      (quote) => new Date(quote.sentDate || quote.createdAt) <= statusDate
    );

    if (relevantQuotations.length === 0) return null;

    // Return the most recent quotation before the status update
    return relevantQuotations.reduce((latest, current) => {
      const latestDate = new Date(latest.sentDate || latest.createdAt);
      const currentDate = new Date(current.sentDate || current.createdAt);
      return currentDate > latestDate ? current : latest;
    });
  };

  // Remove the old handleRecordPayment function and replace it with this:
  const handlePaymentFormChange = (field, value) => {
    setPaymentForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRecordPayment = async (e) => {
    if (e) {
      e.preventDefault(); // Prevent form submission and page refresh
    }

    try {
      if (!selectedQuotation || !lead || !userId) {
        console.error("❌ Missing required data:", {
          selectedQuotation: !!selectedQuotation,
          lead: !!lead,
          userId: !!userId,
        });
        showAlert("error", "Required data missing for payment");
        return;
      }

      setProcessingPayment(true);

      // Basic validation
      const paymentAmount = parseFloat(paymentForm.amount);
      if (!paymentAmount || paymentAmount <= 0) {
        showAlert("error", "Please enter a valid payment amount");
        setProcessingPayment(false);
        return;
      }

      const totalAmount =
        selectedQuotation.finalAmount || selectedQuotation.totalAmount || 0;
      const paidAmount = selectedQuotation.totalReceived || 0;
      const remainingAmount =
        selectedQuotation.remainingAmount || totalAmount - paidAmount;

      if (paymentAmount > remainingAmount) {
        showAlert(
          "error",
          `Payment amount cannot exceed remaining balance of ₹${remainingAmount.toLocaleString()}`
        );
        setProcessingPayment(false);
        return;
      }

      const paymentData = {
        leadId: lead._id,
        quotationId: selectedQuotation._id,
        receivedBy: userId,
        amount: paymentAmount,
        paymentType: paymentForm.paymentType,
        paymentMethod: paymentForm.paymentMethod,
        remarks: paymentForm.remarks,
        nextPaymentDate: paymentForm.nextPaymentDate,
      };

      console.log("📤 Sending payment request:", paymentData);

      const response = await fetch(`${url}/api/leads/record`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(paymentData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`
        );
      }

      if (!data.success) {
        throw new Error(data.message || "Payment recording failed");
      }

      console.log("✅ Payment recorded successfully:", data);

      // IMPORTANT: Refresh the entire lead data to get updated amounts
      await fetchLeadDetails();

      handleClosePaymentDialog();
      showAlert("success", "Payment recorded successfully!");
    } catch (error) {
      console.error("❌ Error recording payment:", error);
      showAlert("error", `Error recording payment: ${error.message}`);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Payment dialog functions
  // Payment dialog functions
  const handleOpenPaymentDialog = useCallback(
    (quotation) => {
      if (!quotation || !lead || !userId) {
        showAlert("error", "Required data missing for payment");
        return;
      }

      // 🚨 UPDATED: Allow 'sent' and 'revised' statuses too
      if (!["approved", "sent", "revised"].includes(quotation.status)) {
        showAlert(
          "warning",
          "Only approved or shared quotations can accept payments"
        );
        return;
      }

      setSelectedQuotation(quotation);
      setPaymentDialogOpen(true);
    },
    [lead, userId]
  );

  const handleClosePaymentDialog = useCallback(() => {
    setPaymentDialogOpen(false);
    // Don't reset immediately to avoid flicker
    setTimeout(() => setSelectedQuotation(null), 300);
  }, []);

  // Calculate service total for a quotation
  const calculateQuotationTotal = (quotation) => {
    if (!quotation.services || !Array.isArray(quotation.services)) return 0;
    return quotation.services.reduce((total, service) => {
      return total + (service.calculatedTotal || 0);
    }, 0);
  };

  // Get total received amount for a quotation
  const getTotalReceived = (quotation) => {
    if (!quotation.payments || !Array.isArray(quotation.payments)) return 0;
    return quotation.payments.reduce(
      (total, payment) => total + payment.amount,
      0
    );
  };

  // Get remaining amount for a quotation
  const getRemainingAmount = (quotation) => {
    const total = quotation.finalAmount || calculateQuotationTotal(quotation);
    const received = getTotalReceived(quotation);
    return total - received;
  };

  // Check if record has meeting details
  const hasMeetingDetails = (record) => {
    return !!(
      record.meetingPlatform ||
      record.meetingLink ||
      record.location ||
      record.dateTime ||
      record.platform ||
      record.link ||
      record.location ||
      record.dateTime
    );
  };

  // Find matching remark for a status record
  const findMatchingRemark = (statusRecord, allRemarks) => {
    if (!allRemarks || !statusRecord) return null;

    return allRemarks.find((remark) => {
      const timeDiff = Math.abs(
        new Date(statusRecord.dateTime || statusRecord.updatedAt) -
        new Date(remark.date)
      );
      const isSameUser = remark.addedBy === statusRecord.updatedBy;
      const isCloseTime = timeDiff < 60000; // Within 1 minute

      return isSameUser && isCloseTime;
    });
  };

  // Get meeting platform icon
  const getMeetingPlatformIcon = (platform) => {
    if (!platform) return <Videocam />;

    const platformLower = platform.toLowerCase();
    if (platformLower.includes("zoom")) return <Videocam />;
    if (platformLower.includes("google") || platformLower.includes("meet"))
      return <Videocam />;
    if (platformLower.includes("teams")) return <Videocam />;
    if (platformLower.includes("offline") || platformLower.includes("physical"))
      return <LocationOn />;
    return <Videocam />;
  };

  // Component to render meeting details
  const MeetingDetailsSection = ({ event }) => {
    if (!event.hasMeeting) return null;

    return (
      <Box
        sx={{
          mt: 2,
          p: isMobile ? 1.5 : 2,
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(139, 92, 246, 0.1)"
              : "rgba(139, 92, 246, 0.05)",
          borderRadius: 2,
          border: `1px solid ${theme.palette.mode === "dark"
            ? "rgba(139, 92, 246, 0.3)"
            : "rgba(139, 92, 246, 0.2)"
            }`,
        }}
      >
        <Typography
          variant={isMobile ? "body2" : "subtitle2"}
          sx={{
            fontWeight: 600,
            color: "#8B5CF6",
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <Videocam fontSize={isMobile ? "small" : "medium"} />
          Meeting Scheduled
        </Typography>

        <Grid container spacing={isMobile ? 1 : 2}>
          {/* Platform */}
          {event.meetingPlatform && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ color: "#8B5CF6", mt: 0.2 }}>
                  {getMeetingPlatformIcon(event.meetingPlatform)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ fontWeight: 500, mb: 0.5 }}
                  >
                    Platform
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    sx={{ fontWeight: 500, color: "text.primary" }}
                  >
                    {platformDisplayMap[event.meetingPlatform] ||
                      event.meetingPlatform}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Date & Time */}
          {event.meetingDateTime && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ color: "#8B5CF6", mt: 0.2 }}>
                  <ScheduleIcon fontSize={isMobile ? "small" : "medium"} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ fontWeight: 500, mb: 0.5 }}
                  >
                    Date & Time
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    sx={{ fontWeight: 500, color: "text.primary" }}
                  >
                    {formatDate(event.meetingDateTime)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >
                    {formatTime(event.meetingDateTime)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Meeting Link */}
          {event.meetingLink && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ color: "#8B5CF6", mt: 0.2 }}>
                  <LinkIcon fontSize={isMobile ? "small" : "medium"} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ fontWeight: 500, mb: 0.5 }}
                  >
                    Meeting Link
                  </Typography>
                  <Link
                    href={
                      event.meetingLink.startsWith("http")
                        ? event.meetingLink
                        : `https://${event.meetingLink}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    variant={isMobile ? "caption" : "body2"}
                    sx={{
                      fontWeight: 500,
                      textDecoration: "none",
                      color: "#8B5CF6",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#7C3AED",
                      },
                    }}
                  >
                    Join Meeting
                  </Link>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Location - only show if it's an offline meeting */}
          {event.meetingLocation &&
            (event.meetingPlatform === "offline" ||
              event.meetingPlatform === "physical") && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ color: "#8B5CF6", mt: 0.2 }}>
                    <LocationOn fontSize={isMobile ? "small" : "medium"} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ fontWeight: 500, mb: 0.5 }}
                    >
                      Meeting Location
                    </Typography>
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      sx={{ fontWeight: 500, color: "text.primary" }}
                    >
                      {event.meetingLocation}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

          {/* Meeting Remarks */}
          {event.meetingRemarks && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ color: "#8B5CF6", mt: 0.2 }}>
                    <CommentIcon fontSize={isMobile ? "small" : "medium"} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ fontWeight: 500, mb: 0.5 }}
                    >
                      Additional Remarks
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(0, 0, 0, 0.2)"
                            : "rgba(0, 0, 0, 0.02)",
                        borderRadius: 1,
                        borderLeft: `3px solid #8B5CF6`,
                      }}
                    >
                      <Typography
                        variant={isMobile ? "caption" : "body2"}
                        sx={{
                          fontStyle: "italic",
                          color: "text.primary",
                          lineHeight: 1.5,
                        }}
                      >
                        "{event.meetingRemarks}"
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  // In your LeadDetails.js - Update the PaymentDialog component
  // const PaymentDialog = React.memo(({
  //   open,
  //   onClose,
  //   quotation,
  //   lead,
  //   userId
  // }) => {
  //   const [processingPayment, setProcessingPayment] = useState(false);
  //   const [localPaymentForm, setLocalPaymentForm] = useState({
  //     amount: '',
  //     paymentType: 'token',
  //     paymentMethod: 'bank_transfer',
  //     remarks: '',
  //     nextPaymentDate: ''
  //   });

  //   // Reset form when dialog opens - FIXED: Use local state instead of parent state
  //   useEffect(() => {
  //     if (open && quotation) {
  //       const defaultNextDate = new Date();
  //       defaultNextDate.setDate(defaultNextDate.getDate() + 7);

  //       setLocalPaymentForm({
  //         amount: '',
  //         paymentType: 'token',
  //         paymentMethod: 'bank_transfer',
  //         remarks: '',
  //         nextPaymentDate: defaultNextDate.toISOString().split('T')[0]
  //       });
  //       setProcessingPayment(false);
  //     }
  //   }, [open, quotation]);

  //   const handleLocalPaymentFormChange = (field, value) => {
  //     setLocalPaymentForm(prev => ({ ...prev, [field]: value }));
  //   };

  //   // const handleFormSubmit = async (e) => {
  //   //   e.preventDefault();

  //   //   // Validate form
  //   //   if (!localPaymentForm.amount || parseFloat(localPaymentForm.amount) <= 0) {
  //   //     showAlert('error', 'Please enter a valid payment amount');
  //   //     return;
  //   //   }

  //   //   if (['token', 'partially_paid'].includes(localPaymentForm.paymentType) && !localPaymentForm.nextPaymentDate) {
  //   //     showAlert('error', 'Please select next payment date for token or partial payments');
  //   //     return;
  //   //   }

  //   //   setProcessingPayment(true);

  //   //   try {
  //   //     const paymentData = {
  //   //       leadId: lead._id,
  //   //       quotationId: quotation._id,
  //   //       receivedBy: userId,
  //   //       amount: parseFloat(localPaymentForm.amount),
  //   //       paymentType: localPaymentForm.paymentType,
  //   //       paymentMethod: localPaymentForm.paymentMethod,
  //   //       remarks: localPaymentForm.remarks,
  //   //       nextPaymentDate: localPaymentForm.nextPaymentDate
  //   //     };

  //   //     console.log("📤 Sending payment request:", paymentData);

  //   //     const response = await fetch(`${url}/api/leads/record`, {
  //   //       method: 'POST',
  //   //       headers: { 'Content-Type': 'application/json' },
  //   //       body: JSON.stringify(paymentData)
  //   //     });

  //   //     const data = await response.json();

  //   //     if (!response.ok) {
  //   //       throw new Error(data.message || `HTTP error! status: ${response.status}`);
  //   //     }

  //   //     if (!data.success) {
  //   //       throw new Error(data.message || 'Payment recording failed');
  //   //     }

  //   //     console.log("✅ Payment recorded successfully:", data);

  //   //     // Refresh data and close dialog
  //   //     await fetchLeadDetails();
  //   //     onClose();
  //   //     showAlert('success', 'Payment recorded successfully!');

  //   //   } catch (error) {
  //   //     console.error('❌ Error recording payment:', error);
  //   //     showAlert('error', `Error recording payment: ${error.message}`);
  //   //   } finally {
  //   //     setProcessingPayment(false);
  //   //   }
  //   // };
  // const handleFormSubmit = async (e) => {
  //   e.preventDefault();

  //   // Validate form
  //   if (!localPaymentForm.amount || parseFloat(localPaymentForm.amount) <= 0) {
  //     showAlert('error', 'Please enter a valid payment amount');
  //     return;
  //   }

  //   if (['token', 'partially_paid'].includes(localPaymentForm.paymentType) && !localPaymentForm.nextPaymentDate) {
  //     showAlert('error', 'Please select next payment date for token or partial payments');
  //     return;
  //   }

  //   setProcessingPayment(true);

  //   try {
  //     const paymentData = {
  //       leadId: lead._id,
  //       quotationId: quotation._id,
  //       receivedBy: userId,
  //       amount: parseFloat(localPaymentForm.amount),
  //       paymentType: localPaymentForm.paymentType,
  //       paymentMethod: localPaymentForm.paymentMethod,
  //       remarks: localPaymentForm.remarks,
  //       nextPaymentDate: localPaymentForm.nextPaymentDate
  //     };

  //     console.log("📤 Sending payment request:", paymentData);

  //     const response = await fetch(`${url}/api/leads/record`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(paymentData)
  //     });

  //     const data = await response.json();

  //     if (!response.ok) {
  //       throw new Error(data.message || `HTTP error! status: ${response.status}`);
  //     }

  //     if (!data.success) {
  //       throw new Error(data.message || 'Payment recording failed');
  //     }

  //     console.log("✅ Payment recorded successfully:", data);

  //     // IMPORTANT: Show success message with status change info
  //     const statusChangeMessage = data.data.lead.status !== lead.status
  //       ? ` and lead status updated to ${statusDisplayMap[data.data.lead.status] || data.data.lead.status}`
  //       : '';

  //     // Refresh data and close dialog
  //     await fetchLeadDetails();
  //     onClose();
  //     showAlert('success', `Payment recorded successfully!${statusChangeMessage}`);

  //   } catch (error) {
  //     console.error('❌ Error recording payment:', error);
  //     showAlert('error', `Error recording payment: ${error.message}`);
  //   } finally {
  //     setProcessingPayment(false);
  //   }
  // };
  //   if (!quotation) return null;

  //   // Calculate amounts
  //   const totalAmount = quotation.finalAmount || quotation.totalAmount || 0;
  //   const paidAmount = quotation.totalReceived || 0;
  //   const currentBalance = quotation.remainingAmount || (totalAmount - paidAmount);
  //   const paymentAmount = parseFloat(localPaymentForm.amount) || 0;
  //   const remainingAfterPayment = currentBalance - paymentAmount;

  //   // Check if payment is overdue
  //   const isOverdue = quotation.paymentStatus === 'overdue';
  //   const hasOverduePayments = quotation.payments?.some(payment => {
  //     if (!payment.nextPaymentDate) return false;
  //     const nextDate = new Date(payment.nextPaymentDate);
  //     return nextDate < new Date() && quotation.paymentStatus !== 'fully_paid';
  //   });

  //   return (
  //     <Dialog
  //       open={open}
  //       onClose={onClose}
  //       maxWidth="sm"
  //       fullWidth
  //       PaperProps={{
  //         sx: {
  //           borderRadius: 2,
  //           backgroundColor: 'white'
  //         }
  //       }}
  //     >
  //       {/* Header */}
  //       <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'grey.200' }}>
  //         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
  //           <AttachMoney />
  //           <Typography variant="h6" sx={{ fontWeight: 600 }}>
  //             Record Payment
  //           </Typography>
  //         </Box>

  //         {/* Overdue Status Badge */}
  //         {(isOverdue || hasOverduePayments) && (
  //           <Chip
  //             label="OVERDUE PAYMENT"
  //             color="error"
  //             size="small"
  //             sx={{ mt: 1, fontWeight: 'bold' }}
  //           />
  //         )}
  //       </Box>

  //       {/* Content */}
  //       <DialogContent sx={{ p: 0 }}>
  //         <Box sx={{ p: 3, backgroundColor: 'grey.50' }}>

  //           {/* Quotation Summary */}
  //           <Box sx={{
  //             mb: 3,
  //             p: 2.5,
  //             bgcolor: 'white',
  //             borderRadius: 2,
  //             border: '1px solid',
  //             borderColor: isOverdue ? 'error.main' : 'grey.200',
  //             boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  //           }}>
  //             <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
  //               Quotation Summary
  //             </Typography>

  //             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
  //               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  //                 <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
  //                 <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{totalAmount.toLocaleString()}</Typography>
  //               </Box>
  //               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  //                 <Typography variant="body2" color="text.secondary">Already Paid:</Typography>
  //                 <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>₹{paidAmount.toLocaleString()}</Typography>
  //               </Box>
  //               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  //                 <Typography variant="body2" color="text.secondary">Current Balance:</Typography>
  //                 <Typography variant="body2" sx={{
  //                   fontWeight: 600,
  //                   color: isOverdue ? 'error.main' : 'warning.main'
  //                 }}>
  //                   ₹{currentBalance.toLocaleString()}
  //                 </Typography>
  //               </Box>

  //               {/* Payment Status */}
  //               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  //                 <Typography variant="body2" color="text.secondary">Payment Status:</Typography>
  //                 <Chip
  //                   label={quotation.paymentStatus?.toUpperCase() || 'PENDING'}
  //                   color={
  //                     quotation.paymentStatus === 'fully_paid' ? 'success' :
  //                     quotation.paymentStatus === 'overdue' ? 'error' :
  //                     quotation.paymentStatus === 'partially_paid' ? 'warning' : 'default'
  //                   }
  //                   size="small"
  //                 />
  //               </Box>

  //               {localPaymentForm.amount && (
  //                 <>
  //                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  //                     <Typography variant="body2" color="text.secondary">This Payment:</Typography>
  //                     <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>₹{paymentAmount.toLocaleString()}</Typography>
  //                   </Box>
  //                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  //                     <Typography variant="body2" color="text.secondary">Remaining After:</Typography>
  //                     <Typography variant="body2" sx={{
  //                       fontWeight: 600,
  //                       color: remainingAfterPayment === 0 ? 'success.main' : 'warning.main'
  //                     }}>
  //                       ₹{remainingAfterPayment.toLocaleString()}
  //                     </Typography>
  //                   </Box>
  //                 </>
  //               )}
  //             </Box>
  //           </Box>

  //           {/* Payment Recording Form */}
  //           <Box
  //             component="form"
  //             onSubmit={handleFormSubmit}
  //             id="payment-form"
  //             sx={{
  //               bgcolor: 'white',
  //               p: 2.5,
  //               borderRadius: 2,
  //               border: '1px solid',
  //               borderColor: 'grey.200',
  //               boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  //             }}
  //           >
  //             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
  //               <FormControl fullWidth>
  //                 <InputLabel>Payment Type *</InputLabel>
  //                 <Select
  //                   value={localPaymentForm.paymentType}
  //                   label="Payment Type *"
  //                   onChange={(e) => handleLocalPaymentFormChange('paymentType', e.target.value)}
  //                 >
  //                   <MenuItem value="token">Token Amount</MenuItem>
  //                   <MenuItem value="partially_paid">Partial Payment</MenuItem>
  //                   <MenuItem value="fully_paid">Full Payment</MenuItem>
  //                 </Select>
  //               </FormControl>

  //               <TextField
  //                 fullWidth
  //                 label="Amount *"
  //                 type="number"
  //                 value={localPaymentForm.amount}
  //                 onChange={(e) => handleLocalPaymentFormChange('amount', e.target.value)}
  //                 InputProps={{
  //                   startAdornment: <InputAdornment position="start">₹</InputAdornment>,
  //                 }}
  //                 helperText={
  //                   localPaymentForm.amount ?
  //                   `Remaining after payment: ₹${remainingAfterPayment.toLocaleString()}` :
  //                   `Maximum: ₹${currentBalance.toLocaleString()}`
  //                 }
  //                 error={paymentAmount > currentBalance}
  //               />

  //               <FormControl fullWidth>
  //                 <InputLabel>Payment Method *</InputLabel>
  //                 <Select
  //                   value={localPaymentForm.paymentMethod}
  //                   label="Payment Method *"
  //                   onChange={(e) => handleLocalPaymentFormChange('paymentMethod', e.target.value)}
  //                 >
  //                   <MenuItem value="cash">Cash</MenuItem>
  //                   <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
  //                   <MenuItem value="cheque">Cheque</MenuItem>
  //                   <MenuItem value="online">Online Payment</MenuItem>
  //                   <MenuItem value="card">Card</MenuItem>
  //                 </Select>
  //               </FormControl>

  //               {['token', 'partially_paid'].includes(localPaymentForm.paymentType) && (
  //                 <TextField
  //                   fullWidth
  //                   label="Next Payment Date *"
  //                   type="date"
  //                   value={localPaymentForm.nextPaymentDate}
  //                   onChange={(e) => handleLocalPaymentFormChange('nextPaymentDate', e.target.value)}
  //                   InputLabelProps={{ shrink: true }}
  //                 />
  //               )}

  //               <TextField
  //                 fullWidth
  //                 label="Remarks"
  //                 multiline
  //                 rows={2}
  //                 value={localPaymentForm.remarks}
  //                 onChange={(e) => handleLocalPaymentFormChange('remarks', e.target.value)}
  //                 placeholder="Payment notes or reference..."
  //               />
  //             </Box>
  //           </Box>
  //         </Box>
  //       </DialogContent>

  //       {/* Footer Actions */}
  //       <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
  //         <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
  //           <Button
  //             onClick={onClose}
  //             color="inherit"
  //             sx={{ minWidth: 100 }}
  //           >
  //             Cancel
  //           </Button>
  //           <Button
  //             type="submit"
  //             form="payment-form"
  //             variant="contained"
  //             color="success"
  //             disabled={
  //               !localPaymentForm.amount ||
  //               processingPayment ||
  //               paymentAmount > currentBalance ||
  //               paymentAmount <= 0 ||
  //               (['token', 'partially_paid'].includes(localPaymentForm.paymentType) && !localPaymentForm.nextPaymentDate)
  //             }
  //             startIcon={processingPayment ? <CircularProgress size={20} /> : <AttachMoney />}
  //             sx={{ minWidth: 160 }}
  //           >
  //             {processingPayment ? 'Processing...' : 'Record Payment'}
  //           </Button>
  //         </Box>
  //       </DialogActions>
  //     </Dialog>
  //   );
  // });
  // In your LeadDetails.js - Update the PaymentDialog component
  const PaymentDialog = React.memo(
    ({ open, onClose, quotation, lead, userId }) => {
      const [processingPayment, setProcessingPayment] = useState(false);
      const [localPaymentForm, setLocalPaymentForm] = useState({
        amount: "",
        paymentType: "token",
        paymentMethod: "bank_transfer",
        remarks: "",
        nextPaymentDate: "",
      });

      // Check if payment is overdue
      const isOverduePayment =
        quotation?.paymentStatus === "overdue" ||
        lead?.status === "payment_overdue";

      // Reset form when dialog opens
      useEffect(() => {
        if (open && quotation) {
          const defaultNextDate = new Date();
          defaultNextDate.setDate(defaultNextDate.getDate() + 7);

          setLocalPaymentForm({
            amount: "",
            paymentType: "token",
            paymentMethod: "bank_transfer",
            remarks: "",
            nextPaymentDate: isOverduePayment
              ? ""
              : defaultNextDate.toISOString().split("T")[0],
          });
          setProcessingPayment(false);
        }
      }, [open, quotation, isOverduePayment]);

      const handleLocalPaymentFormChange = (field, value) => {
        setLocalPaymentForm((prev) => ({ ...prev, [field]: value }));
      };

      const handleFormSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        if (
          !localPaymentForm.amount ||
          parseFloat(localPaymentForm.amount) <= 0
        ) {
          showAlert("error", "Please enter a valid payment amount");
          return;
        }

        // 🚨 IMPORTANT: Make nextPaymentDate optional for overdue payments
        // Only require nextPaymentDate for non-overdue token/partial payments
        if (
          !isOverduePayment &&
          ["token", "partially_paid"].includes(localPaymentForm.paymentType) &&
          !localPaymentForm.nextPaymentDate
        ) {
          showAlert(
            "error",
            "Please select next payment date for token or partial payments"
          );
          return;
        }

        setProcessingPayment(true);

        try {
          const paymentData = {
            leadId: lead._id,
            quotationId: quotation._id,
            receivedBy: userId,
            amount: parseFloat(localPaymentForm.amount),
            paymentType: localPaymentForm.paymentType,
            paymentMethod: localPaymentForm.paymentMethod,
            remarks: localPaymentForm.remarks,
            nextPaymentDate: localPaymentForm.nextPaymentDate || null,
          };

          console.log("📤 Sending payment request:", {
            isOverduePayment,
            paymentData,
          });

          const response = await fetch(`${url}/api/leads/record`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(paymentData),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(
              data.message || `HTTP error! status: ${response.status}`
            );
          }

          if (!data.success) {
            throw new Error(data.message || "Payment recording failed");
          }

          console.log("✅ Payment recorded successfully:", data);

          // IMPORTANT: Show success message with status change info
          const statusChangeMessage =
            data.data.lead.status !== lead.status
              ? ` and lead status updated to ${statusDisplayMap[data.data.lead.status] ||
              data.data.lead.status
              }`
              : "";

          // Refresh data and close dialog
          await fetchLeadDetails();
          onClose();
          showAlert(
            "success",
            `Payment recorded successfully!${statusChangeMessage}`
          );
        } catch (error) {
          console.error("❌ Error recording payment:", error);
          showAlert("error", `Error recording payment: ${error.message}`);
        } finally {
          setProcessingPayment(false);
        }
      };

      if (!quotation) return null;

      // Calculate amounts
      const totalAmount = quotation.finalAmount || quotation.totalAmount || 0;
      const paidAmount = quotation.totalReceived || 0;
      const currentBalance =
        quotation.remainingAmount || totalAmount - paidAmount;
      const paymentAmount = parseFloat(localPaymentForm.amount) || 0;
      const remainingAfterPayment = currentBalance - paymentAmount;

      // Check if payment is overdue
      const isOverdue = quotation.paymentStatus === "overdue";

      return (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              backgroundColor: "white",
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{ p: 3, borderBottom: "1px solid", borderColor: "grey.200" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AttachMoney />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Record Payment
              </Typography>
            </Box>

            {/* Overdue Status Badge */}
            {isOverdue && (
              <Chip
                label="OVERDUE PAYMENT"
                color="error"
                size="small"
                sx={{ mt: 1, fontWeight: "bold" }}
              />
            )}
          </Box>

          {/* Content */}
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, backgroundColor: "grey.50" }}>
              {/* Quotation Summary */}
              <Box
                sx={{
                  mb: 3,
                  p: 2.5,
                  bgcolor: "white",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isOverdue ? "error.main" : "grey.200",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  Quotation Summary
                </Typography>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Amount:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ₹{totalAmount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Already Paid:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "success.main" }}
                    >
                      ₹{paidAmount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Current Balance:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isOverdue ? "error.main" : "warning.main",
                      }}
                    >
                      ₹{currentBalance.toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Payment Status */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Payment Status:
                    </Typography>
                    <Chip
                      label={
                        quotation.paymentStatus?.toUpperCase() || "PENDING"
                      }
                      color={
                        quotation.paymentStatus === "fully_paid"
                          ? "success"
                          : quotation.paymentStatus === "overdue"
                            ? "error"
                            : quotation.paymentStatus === "partially_paid"
                              ? "warning"
                              : "default"
                      }
                      size="small"
                    />
                  </Box>

                  {localPaymentForm.amount && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          This Payment:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "info.main" }}
                        >
                          ₹{paymentAmount.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Remaining After:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color:
                              remainingAfterPayment === 0
                                ? "success.main"
                                : "warning.main",
                          }}
                        >
                          ₹{remainingAfterPayment.toLocaleString()}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>

              {/* Payment Recording Form */}
              <Box
                component="form"
                onSubmit={handleFormSubmit}
                id="payment-form"
                sx={{
                  bgcolor: "white",
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Type *</InputLabel>
                    <Select
                      value={localPaymentForm.paymentType}
                      label="Payment Type *"
                      onChange={(e) =>
                        handleLocalPaymentFormChange(
                          "paymentType",
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="token">Token Amount (Overdue)</MenuItem>
                      <MenuItem value="partially_paid">
                        Partial Payment (Overdue)
                      </MenuItem>
                      <MenuItem value="fully_paid">Full Payment</MenuItem>
                    </Select>
                    <Typography
                      variant="caption"
                      color="error.main"
                      sx={{ mt: 1, fontWeight: 500, display: 'block' }}
                    >
                      Note: Any payment less than the full amount will mark the lead as "Overdue" until fully paid.
                    </Typography>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Amount *"
                    type="number"
                    value={localPaymentForm.amount}
                    onChange={(e) =>
                      handleLocalPaymentFormChange("amount", e.target.value)
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    helperText={
                      localPaymentForm.amount
                        ? `Remaining after payment: ₹${remainingAfterPayment.toLocaleString()}`
                        : `Maximum: ₹${currentBalance.toLocaleString()}`
                    }
                    error={paymentAmount > currentBalance}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Payment Method *</InputLabel>
                    <Select
                      value={localPaymentForm.paymentMethod}
                      label="Payment Method *"
                      onChange={(e) =>
                        handleLocalPaymentFormChange(
                          "paymentMethod",
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      <MenuItem value="cheque">Cheque</MenuItem>
                      <MenuItem value="online">Online Payment</MenuItem>
                      <MenuItem value="card">Card</MenuItem>
                    </Select>
                  </FormControl>

                  {/* 🚨 CONDITIONAL NEXT PAYMENT DATE FIELD */}
                  {(isOverduePayment &&
                    ["token", "partially_paid"].includes(
                      localPaymentForm.paymentType
                    )) ||
                    (!isOverduePayment &&
                      ["token", "partially_paid"].includes(
                        localPaymentForm.paymentType
                      )) ? (
                    <TextField
                      fullWidth
                      label={
                        isOverduePayment
                          ? "Next Payment Date (Optional)"
                          : "Next Payment Date *"
                      }
                      type="date"
                      value={localPaymentForm.nextPaymentDate}
                      onChange={(e) =>
                        handleLocalPaymentFormChange(
                          "nextPaymentDate",
                          e.target.value
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                      helperText={
                        isOverduePayment
                          ? "Optional: Add if payment is partial and you want to schedule next payment"
                          : "Required for token or partial payments"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: isOverduePayment
                            ? "#fff3cd"
                            : "white",
                        },
                      }}
                    />
                  ) : null}

                  <TextField
                    fullWidth
                    label="Remarks"
                    multiline
                    rows={2}
                    value={localPaymentForm.remarks}
                    onChange={(e) =>
                      handleLocalPaymentFormChange("remarks", e.target.value)
                    }
                    placeholder="Payment notes or reference..."
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>

          {/* Footer Actions */}
          <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={onClose} color="inherit" sx={{ minWidth: 100 }}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="payment-form"
                variant="contained"
                color="success"
                disabled={
                  !localPaymentForm.amount ||
                  processingPayment ||
                  paymentAmount > currentBalance ||
                  paymentAmount <= 0 ||
                  (!isOverduePayment &&
                    ["token", "partially_paid"].includes(
                      localPaymentForm.paymentType
                    ) &&
                    !localPaymentForm.nextPaymentDate)
                }
                startIcon={
                  processingPayment ? (
                    <CircularProgress size={20} />
                  ) : (
                    <AttachMoney />
                  )
                }
                sx={{ minWidth: 160 }}
              >
                {processingPayment ? "Processing..." : "Record Payment"}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      );
    }
  );
  // Check if lead has shared/approved quotations
  const hasSharedOrApprovedQuotations = useMemo(() => {
    if (!lead?.quotations || lead.quotations.length === 0) return false;

    return lead.quotations.some((quotation) => {
      const allowedStatuses = [
        "sent",
        "approved",
        "quotation_approved",
        "revised",
      ];
      return allowedStatuses.includes(quotation.status);
    });
  }, [lead]);

  // Merge all activities into a single timeline
  const timelineData = useMemo(() => {
    if (!lead) return { events: [], groupedByDate: {}, groupedByMonth: {} };

    const events = [];
    const processedRemarks = new Set();

    // Process status records
    lead.statusRecords?.forEach((record, index) => {
      if (!record.status && !record.callStatus) return;

      const matchingRemark = findMatchingRemark(record, lead.remarks);
      let title = "";
      let previousStatus = "";

      if (index > 0) {
        previousStatus =
          statusDisplayMap[lead.statusRecords[index - 1].status] ||
          lead.statusRecords[index - 1].status;
      }

      const currentStatus = statusDisplayMap[record.status] || record.status;

      if (previousStatus && currentStatus !== previousStatus) {
        title = `${previousStatus} → ${currentStatus}`;
      } else if (currentStatus && currentStatus !== "new") {
        title = `Status set to ${currentStatus}`;
      } else {
        return;
      }

      if (record.callStatus && record.callStatus !== "not_called") {
        const callStatusDisplay =
          statusDisplayMap[record.callStatus] || record.callStatus;
        title += ` | Call: ${callStatusDisplay}`;
      }

      if (record.leadStatus && record.leadStatus !== record.status) {
        const leadStatusDisplay =
          statusDisplayMap[record.leadStatus] || record.leadStatus;
        title += ` | Lead: ${leadStatusDisplay}`;
      }

      let combinedRemark = "";
      if (
        matchingRemark &&
        matchingRemark.remarks &&
        matchingRemark.remarks.trim()
      ) {
        combinedRemark = matchingRemark.remarks;
        processedRemarks.add(matchingRemark._id || matchingRemark.date);
      }

      const hasMeeting = hasMeetingDetails(record);

      events.push({
        id: `status-${record._id || record.dateTime || record.updatedAt}`,
        type: "status",
        date: record.dateTime || record.updatedAt,
        title: title,
        user: userNames[record.updatedBy],
        details: record,
        status: record.status,
        previousStatus: previousStatus,
        currentStatus: currentStatus,
        callStatus: record.callStatus,
        leadStatus: record.leadStatus,
        hasMeeting: hasMeeting,
        hasStatusFlow: !!previousStatus,
        remark: combinedRemark,
        isCombined: !!combinedRemark,
        matchingRemarkId: matchingRemark?._id,
        meetingPlatform: record.meetingPlatform || record.platform,
        meetingLink: record.meetingLink || record.link,
        meetingLocation: record.location,
        meetingDateTime: record.dateTime,
        meetingRemarks: record.meetingRemarks || record.remarks,
      });
    });

    // Add main lead meeting as a separate event
    if (lead.meeting && hasMeetingDetails(lead.meeting)) {
      const hasActualMeeting =
        lead.meeting.dateTime &&
        lead.meeting.dateTime !== lead.createdAt &&
        lead.meeting.dateTime !== lead.updatedAt;

      if (hasActualMeeting) {
        events.push({
          id: `meeting-${lead._id}`,
          type: "meeting",
          date: lead.meeting.dateTime,
          title: "Meeting Scheduled",
          user: userNames[lead.addedBy] || "System",
          details: lead.meeting,
          hasMeeting: true,
          meetingPlatform: lead.meeting.platform,
          meetingLink: lead.meeting.link,
          meetingLocation: lead.meeting.location,
          meetingDateTime: lead.meeting.dateTime,
          meetingRemarks: lead.meeting.remarks,
        });
      }
    }

    // Add standalone remarks
    lead.remarks?.forEach((remark) => {
      if (processedRemarks.has(remark._id || remark.date)) return;

      if (remark.remarks && remark.remarks.trim()) {
        events.push({
          id: `remark-${remark._id || remark.date}`,
          type: "remark",
          date: remark.date,
          title: remark.remarks,
          user: userNames[remark.addedBy],
          details: remark,
          isStandalone: true,
        });
      }
    });

    // Add quotations to timeline
    lead.quotations?.forEach((quotation) => {
      const quotationEvent = {
        id: `quotation-${quotation._id || quotation.sentDate}`,
        type: "quotation",
        date: quotation.sentDate || quotation.createdAt,
        title: `Quotation ${quotation.status === "sent"
          ? "Sent"
          : quotation.status === "revised"
            ? "Revised"
            : quotation.status
          }: ${quotation.quotationNumber}`,
        user: "System",
        details: quotation,
        quotationNumber: quotation.quotationNumber,
        status: quotation.status,
        totalAmount: quotation.totalAmount,
        finalAmount: quotation.finalAmount,
        hasPDF: true,
        isRevised: quotation.isRevised || false,
        revisionNumber: quotation.revisionCount || 0,
        pdfUrl: quotation.pdfUrl,
      };

      events.push(quotationEvent);

      // Add payment records to timeline
      quotation.payments?.forEach((payment, index) => {
        events.push({
          id: `payment-${quotation._id}-${index}`,
          type: "payment",
          date: payment.paymentDate,
          title: `Payment Received: ₹${payment.amount.toLocaleString()} - ${paymentTypeMap[payment.paymentType]
            }`,
          user: userNames[payment.receivedBy],
          details: payment,
          amount: payment.amount,
          paymentType: payment.paymentType,
          paymentMethod: payment.paymentMethod,
          remarks: payment.remarks,
          nextPaymentDate: payment.nextPaymentDate,
        });
      });
    });

    // Sort by date (newest first)
    const sortedEvents = events.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );

    // Group by date first
    const groupedByDate = {};
    sortedEvents.forEach((event) => {
      const dateKey = formatDate(event.date);
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = [];
      }
      groupedByDate[dateKey].push(event);
    });

    // Then group by month
    const groupedByMonth = {};
    Object.entries(groupedByDate).forEach(([dateKey, dateEvents]) => {
      const date = new Date(dateEvents[0].date);
      const monthYear = date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });

      if (!groupedByMonth[monthYear]) {
        groupedByMonth[monthYear] = {};
      }
      groupedByMonth[monthYear][dateKey] = dateEvents;
    });

    return { events: sortedEvents, groupedByDate, groupedByMonth };
  }, [lead, userNames, statusDisplayMap]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="h6" color="error" gutterBottom>
          No lead found
        </Typography>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: isSmallMobile ? 1 : isMobile ? 1.5 : 2,
        bgcolor: "background.default",
        minHeight: "100vh",
        maxWidth: "100%",
        overflowX: "hidden",
        mt: -3,
      }}
    >
      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Header with Back Button */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          size={isMobile ? "small" : "medium"}
          sx={{
            fontSize: isSmallMobile
              ? "0.75rem"
              : isMobile
                ? "0.8rem"
                : "0.875rem",
            px: isSmallMobile ? 1 : 2,
          }}
        >
          Back to Leads
        </Button>

        <Box sx={{ display: "flex", gap: 1 }}>
          {editMode && (
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancelEdit}
              color="error"
              size={isMobile ? "small" : "medium"}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
          <Button
            variant={editMode ? "contained" : "outlined"}
            startIcon={
              editMode ? (
                saving ? (
                  <CircularProgress size={16} />
                ) : (
                  <Save />
                )
              ) : (
                <Edit />
              )
            }
            onClick={handleEditToggle}
            color={editMode ? "success" : "primary"}
            size={isMobile ? "small" : "medium"}
            disabled={saving}
          >
            {editMode ? (saving ? "Saving..." : "Save Changes") : "Edit Lead"}
          </Button>
        </Box>
      </Box>

      {/* Client Details Card */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: 3,
          overflow: "visible",
          border: editMode ? `2px solid ${theme.palette.primary.main}` : "none",
          transition: "all 0.3s ease",
        }}
      >
        <CardContent sx={{ p: isSmallMobile ? 1 : isMobile ? 1.5 : 2 }}>
          {" "}
          {/* Reduced padding */}
          {/* Header Section with Edit Indicator */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2, // Reduced margin
              flexDirection: isSmallMobile ? "column" : "row",
              gap: isSmallMobile ? 1 : 1.5, // Reduced gap
            }}
          >
            {/* Left Side - Client Avatar and Name */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5, // Reduced gap
                flex: 1,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: editMode ? "success.main" : "primary.main",
                  width: isSmallMobile ? 36 : isMobile ? 40 : 44, // Reduced size
                  height: isSmallMobile ? 36 : isMobile ? 40 : 44, // Reduced size
                  fontSize: isSmallMobile ? "0.9rem" : "1rem", // Reduced font
                  transition: "all 0.3s ease",
                }}
              >
                <PersonIcon fontSize={isSmallMobile ? "small" : "medium"} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {editMode ? (
                  <TextField
                    fullWidth
                    value={editedLead.clientName || ""}
                    onChange={(e) =>
                      setEditedLead({
                        ...editedLead,
                        clientName: e.target.value,
                      })
                    }
                    variant="outlined"
                    size="small"
                    label="Client Name"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "background.paper",
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant={isSmallMobile ? "h6" : isMobile ? "h6" : "h6"} // Reduced variant
                    sx={{
                      fontWeight: 600,
                      wordBreak: "break-word",
                      color: "text.primary",
                    }}
                  >
                    {lead.clientName}
                  </Typography>
                )}
                <Typography
                  variant={isSmallMobile ? "caption" : "caption"} // Reduced variant
                  color={editMode ? "success.main" : "text.secondary"}
                  sx={{
                    fontWeight: editMode ? 600 : 400,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {editMode && <Edit sx={{ fontSize: 12 }} />}{" "}
                  {/* Reduced icon */}
                  Lead Details {editMode ? "(Editing)" : ""}
                </Typography>
              </Box>
            </Box>

            {/* Right Side - Status Chips */}
            {!editMode && (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5, // Reduced gap
                  flexWrap: "wrap",
                  justifyContent: isSmallMobile ? "flex-start" : "flex-end",
                  width: isSmallMobile ? "100%" : "auto",
                  mt: isSmallMobile ? 1 : 0,
                }}
              >
                <Chip
                  size="small" // Always small
                  label={`Status: ${statusDisplayMap[lead.status] || lead.status
                    }`}
                  color="primary"
                  variant="filled"
                />
                <Chip
                  size="small"
                  label={`Lead Status: ${statusDisplayMap[lead.classificationStatus || lead.leadStatus] || lead.classificationStatus || lead.leadStatus || "New"}`}
                  color="secondary"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Quotation: ${statusDisplayMap[lead.quotationStatus] || lead.quotationStatus || "None"}`}
                  color="warning"
                  variant="outlined"
                />
                <Chip
                  size="small" // Always small
                  label={`Call: ${statusDisplayMap[lead.callStatus] || lead.callStatus
                    }`}
                  color="default"
                  variant="outlined"
                />
                <Chip
                  size="small" // Always small
                  label={`Calls: ${lead.noOfTimesCalled}`}
                  color="info"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
          {/* Contact Information - Compact Layout */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isSmallMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: isSmallMobile ? 1.5 : 2, // Reduced gap
              mb: 2, // Reduced margin
            }}
          >
            {/* Left Column - Contact Info */}
            <Box
              sx={{
                flex: 1,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: isSmallMobile ? 1.5 : 2, // Reduced gap
              }}
            >
              {/* Phone Section */}
              <Box
                sx={{
                  p: 1.5, // Reduced padding
                  borderRadius: 1.5, // Slightly smaller radius
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(25, 118, 210, 0.08)"
                      : "rgba(25, 118, 210, 0.04)",
                  border: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(25, 118, 210, 0.2)"
                    : "rgba(25, 118, 210, 0.1)"
                    }`,
                  minHeight: "80px", // Reduced height
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  {" "}
                  {/* Reduced gap and margin */}
                  <PhoneIcon
                    sx={{ color: "primary.main", fontSize: "small" }}
                  />{" "}
                  {/* Smaller icon */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Contact
                  </Typography>
                </Box>
                {editMode ? (
                  <TextField
                    fullWidth
                    value={editedLead.contactNo || ""}
                    onChange={(e) =>
                      setEditedLead({
                        ...editedLead,
                        contactNo: e.target.value,
                      })
                    }
                    variant="outlined"
                    size="small"
                    placeholder="Enter contact number"
                    sx={{
                      backgroundColor: "background.paper",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "background.paper",
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, wordBreak: "break-word", pl: 2.5 }}
                  >
                    {" "}
                    {/* Reduced padding */}
                    {lead.contactNo}
                  </Typography>
                )}
              </Box>

              {/* Email Section */}
              <Box
                sx={{
                  p: 1.5, // Reduced padding
                  borderRadius: 1.5, // Slightly smaller radius
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(25, 118, 210, 0.08)"
                      : "rgba(25, 118, 210, 0.04)",
                  border: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(25, 118, 210, 0.2)"
                    : "rgba(25, 118, 210, 0.1)"
                    }`,
                  minHeight: "80px", // Reduced height
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  {" "}
                  {/* Reduced gap and margin */}
                  <EmailIcon
                    sx={{ color: "primary.main", fontSize: "small" }}
                  />{" "}
                  {/* Smaller icon */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Email
                  </Typography>
                </Box>
                {editMode ? (
                  <TextField
                    fullWidth
                    value={editedLead.email || ""}
                    onChange={(e) =>
                      setEditedLead({ ...editedLead, email: e.target.value })
                    }
                    variant="outlined"
                    size="small"
                    placeholder="Enter email address"
                    type="email"
                    sx={{
                      backgroundColor: "background.paper",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "background.paper",
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      pl: 2.5, // Reduced padding
                    }}
                  >
                    {lead.email}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Right Column - Services & Sources */}
            <Box
              sx={{
                flex: 1,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: isSmallMobile ? 1.5 : 2, // Reduced gap
              }}
            >
              {/* Services Section */}
              <Box
                sx={{
                  p: 1.5, // Reduced padding
                  borderRadius: 1.5, // Slightly smaller radius
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(156, 39, 176, 0.08)"
                      : "rgba(156, 39, 176, 0.04)",
                  border: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(156, 39, 176, 0.2)"
                    : "rgba(156, 39, 176, 0.1)"
                    }`,
                  minHeight: "80px", // Reduced height
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  {" "}
                  {/* Reduced gap and margin */}
                  <ScheduleIcon
                    sx={{ color: "secondary.main", fontSize: "small" }}
                  />{" "}
                  {/* Smaller icon */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Services
                  </Typography>
                </Box>

                {editMode ? (
                  <Box>
                    {/* Selected Services as Chips */}
                    {editedLead.serviceInterested?.length > 0 && (
                      <Box
                        sx={{
                          mb: 1,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}
                      >
                        {editedLead.serviceInterested.map((service, index) => (
                          <Chip
                            key={index}
                            label={service.service}
                            onDelete={() => removeService(index)}
                            color="secondary"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Box>
                    )}

                    {/* Add Service Form */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      {" "}
                      {/* Reduced gap */}
                      <FormControl sx={{ minWidth: 100 }} size="small">
                        {" "}
                        {/* Reduced width */}
                        <InputLabel>Service</InputLabel>
                        <Select
                          value={currentService.service}
                          onChange={(e) =>
                            setCurrentService({
                              ...currentService,
                              service: e.target.value,
                            })
                          }
                          label="Service"
                        >
                          {serviceOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {currentService.service === "Other" && (
                        <TextField
                          label="Specify"
                          value={currentService.otherService}
                          onChange={(e) =>
                            setCurrentService({
                              ...currentService,
                              otherService: e.target.value,
                            })
                          }
                          size="small"
                          sx={{ minWidth: 100 }} // Reduced width
                        />
                      )}
                      <Button
                        startIcon={<Add />}
                        onClick={addService}
                        variant="contained"
                        size="small"
                        sx={{ height: "36px" }} // Reduced height
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    {lead.serviceInterested?.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {lead.serviceInterested.map((service, index) => (
                          <Chip
                            key={index}
                            label={service.service}
                            color="secondary"
                            variant="filled"
                            size="small"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        No services
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              {/* Lead Sources Section */}
              <Box
                sx={{
                  p: 1.5, // Reduced padding
                  borderRadius: 1.5, // Slightly smaller radius
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 152, 0, 0.08)"
                      : "rgba(255, 152, 0, 0.04)",
                  border: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(255, 152, 0, 0.2)"
                    : "rgba(255, 152, 0, 0.1)"
                    }`,
                  minHeight: "80px", // Reduced height
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  {" "}
                  {/* Reduced gap and margin */}
                  <BusinessIcon
                    sx={{ color: "warning.main", fontSize: "small" }}
                  />{" "}
                  {/* Smaller icon */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Sources
                  </Typography>
                </Box>

                {editMode ? (
                  <Box>
                    {/* Selected Sources as Chips */}
                    {editedLead.leadSources?.length > 0 && (
                      <Box
                        sx={{
                          mb: 1,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}
                      >
                        {editedLead.leadSources.map((source, index) => (
                          <Chip
                            key={index}
                            label={source.source}
                            onDelete={() => removeSource(index)}
                            color="warning"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Box>
                    )}

                    {/* Add Source Form */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      {" "}
                      {/* Reduced gap */}
                      <FormControl sx={{ minWidth: 100 }} size="small">
                        {" "}
                        {/* Reduced width */}
                        <InputLabel>Source</InputLabel>
                        <Select
                          value={currentSource.source}
                          onChange={(e) =>
                            setCurrentSource({
                              ...currentSource,
                              source: e.target.value,
                            })
                          }
                          label="Source"
                        >
                          {leadSourceOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {currentSource.source === "Other" && (
                        <TextField
                          label="Specify"
                          value={currentSource.otherSource}
                          onChange={(e) =>
                            setCurrentSource({
                              ...currentSource,
                              otherSource: e.target.value,
                            })
                          }
                          size="small"
                          sx={{ minWidth: 100 }} // Reduced width
                        />
                      )}
                      <Button
                        startIcon={<Add />}
                        onClick={addSource}
                        variant="contained"
                        size="small"
                        sx={{ height: "36px" }} // Reduced height
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    {lead.leadSources?.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {lead.leadSources.map((source, index) => (
                          <Chip
                            key={index}
                            label={source.source}
                            color="warning"
                            variant="filled"
                            size="small"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        No sources
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* Payment Tracking Card - ONLY SHOW IF QUOTATIONS EXIST AND ARE SHARED/APPROVED */}

      {hasSharedOrApprovedQuotations && (
        <Card
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: 3,
            border: hasOverduePayments ? "2px solid #ef4444" : "none",
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: hasOverduePayments ? "error.main" : "primary.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Payment />
              Payment Tracking
              {hasOverduePayments && (
                <Chip
                  label="OVERDUE PAYMENTS"
                  color="error"
                  size="small"
                  sx={{ ml: 1, fontWeight: "bold" }}
                />
              )}
            </Typography>

            {lead.quotations?.map((quotation, index) => {
              // Only show shared/approved quotations
              if (
                !["sent", "approved", "revised", "quotation_approved"].includes(
                  quotation.status
                )
              ) {
                return null;
              }

              const totalAmount =
                quotation.finalAmount || quotation.totalAmount || 0;
              const paidAmount = quotation.totalReceived || 0;
              const remainingAmount =
                quotation.remainingAmount || totalAmount - paidAmount;
              const receivedAmount = totalAmount - remainingAmount;
              const isOverdue = quotation.paymentStatus === "overdue";
              const paymentStatus =
                paymentStatusMap[quotation.paymentStatus] ||
                paymentStatusMap.pending;

              return (
                <Box
                  key={quotation._id}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: "1px solid",
                    borderColor: isOverdue ? "error.main" : "divider",
                    borderRadius: 2,
                    backgroundColor: isOverdue
                      ? paymentStatus.bgColor
                      : "transparent",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Quotation Header with Services */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Quotation #{quotation.quotationNumber}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {new Date(quotation.sentDate).toLocaleDateString()}
                      </Typography>

                      {/* Services for this quotation */}
                      {quotation.services && quotation.services.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            Services Included:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {quotation.services.map((service, serviceIndex) => (
                              <Chip
                                key={serviceIndex}
                                label={service.serviceName}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Chip
                        label={quotation.status}
                        color={
                          quotation.status === "approved" ||
                            quotation.status === "quotation_approved"
                            ? "success"
                            : quotation.status === "rejected"
                              ? "error"
                              : quotation.status === "sent"
                                ? "primary"
                                : "default"
                        }
                        size="small"
                      />
                      <Chip
                        label={paymentStatus.label}
                        color={paymentStatus.color}
                        size="small"
                      />
                    </Box>
                  </Box>

                  {/* Payment Summary */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      gap: 2,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    {/* Total Amount */}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        minWidth: { xs: "45%", sm: "auto" },
                        mb: { xs: 2, sm: 0 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Total Amount
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        ₹{totalAmount.toLocaleString()}
                      </Typography>
                    </Box>

                    {/* Received */}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        minWidth: { xs: "45%", sm: "auto" },
                        mb: { xs: 2, sm: 0 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Received
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "success.main" }}
                      >
                        ₹{receivedAmount.toLocaleString()}
                      </Typography>
                    </Box>

                    {/* Remaining */}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        minWidth: { xs: "45%", sm: "auto" },
                        mb: { xs: 2, sm: 0 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Remaining
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: isOverdue
                            ? "error.main"
                            : remainingAmount > 0
                              ? "warning.main"
                              : "success.main",
                        }}
                      >
                        ₹{remainingAmount.toLocaleString()}
                      </Typography>
                    </Box>

                    {/* Action Buttons */}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        minWidth: { xs: "100%", sm: "auto" },
                        gap: 1,
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AttachMoney />}
                        onClick={() => handleOpenPaymentDialog(quotation)}
                        disabled={!isPaymentAllowed(quotation)}
                        sx={{
                          width: "100%",
                          maxWidth: "160px",
                          minHeight: "40px",
                          mb: 1,
                        }}
                      >
                        {remainingAmount > 0 ? "Add Payment" : "Fully Paid"}
                      </Button>

                      {/* The Mark Overdue button has been removed as payment status 
                            now handles overdue transitions automatically */}
                      {/* )} */}
                    </Box>
                  </Box>

                  {/* Payment History */}
                  {quotation.payments?.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Receipt fontSize="small" />
                        Payment History ({quotation.payments.length} record
                        {quotation.payments.length > 1 ? "s" : ""})
                      </Typography>
                      {quotation.payments.map((payment, paymentIndex) => {
                        const isPaymentOverdue =
                          payment.nextPaymentDate &&
                          new Date(payment.nextPaymentDate) < new Date();
                        const isOverdueRecord =
                          payment.paymentType === "overdue" ||
                          payment.isOverdueRecord;

                        return (
                          <Box
                            key={paymentIndex}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              py: 1.5,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              backgroundColor:
                                isPaymentOverdue || isOverdueRecord
                                  ? "#fee2e2"
                                  : "transparent",
                              borderRadius: 1,
                              px: 1,
                              "&:last-child": { borderBottom: "none" },
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {isOverdueRecord ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Cancel
                                        sx={{
                                          color: "error.main",
                                          fontSize: 18,
                                        }}
                                      />
                                      <span style={{ color: "#ef4444" }}>
                                        Payment Marked Overdue
                                      </span>
                                    </Box>
                                  ) : (
                                    `₹${payment.amount?.toLocaleString()} - ${paymentTypeMap[payment.paymentType] ||
                                    payment.paymentType
                                    }`
                                  )}
                                </Typography>

                                {/* Status Badges */}
                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                  {isPaymentOverdue && !isOverdueRecord && (
                                    <Chip
                                      label="Overdue"
                                      color="error"
                                      size="small"
                                      sx={{ height: 20, fontSize: "0.6rem" }}
                                    />
                                  )}
                                  {isOverdueRecord && (
                                    <Chip
                                      label="Overdue Status"
                                      color="error"
                                      size="small"
                                      sx={{ height: 20, fontSize: "0.6rem" }}
                                    />
                                  )}
                                  {payment.paymentMethod &&
                                    payment.paymentMethod !== "system" && (
                                      <Chip
                                        label={
                                          paymentMethodMap[
                                          payment.paymentMethod
                                          ] || payment.paymentMethod
                                        }
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: "0.6rem" }}
                                      />
                                    )}
                                </Box>
                              </Box>

                              {/* 🚨 SHOW OVERDUE DETAILS */}
                              {isOverdueRecord && payment.overdueDetails && (
                                <Box
                                  sx={{
                                    mb: 1,
                                    p: 1,
                                    bgcolor: "#fee2e2",
                                    borderRadius: 1,
                                    borderLeft: "3px solid #ef4444",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600, color: "#b91c1c" }}
                                  >
                                    Overdue Details:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                      mt: 0.5,
                                    }}
                                  >
                                    <Typography variant="caption">
                                      Approved: ₹
                                      {payment.overdueDetails?.originalApprovedAmount?.toLocaleString() ||
                                        "N/A"}
                                    </Typography>
                                    <Typography variant="caption">
                                      Overdue: ₹
                                      {payment.overdueDetails?.overdueAmount?.toLocaleString() ||
                                        "N/A"}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              {/* Payment Date and Details */}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {new Date(
                                  payment.paymentDate
                                ).toLocaleDateString()}
                                {payment.paymentMethod &&
                                  payment.paymentMethod !== "system" &&
                                  ` • ${paymentMethodMap[payment.paymentMethod] ||
                                  payment.paymentMethod
                                  }`}
                              </Typography>

                              {/* Next Payment Date */}
                              {payment.nextPaymentDate && (
                                <Typography
                                  variant="caption"
                                  color={isPaymentOverdue ? "error" : "primary"}
                                  sx={{
                                    display: "block",
                                    mt: 0.5,
                                    fontWeight: isPaymentOverdue ? 600 : 400,
                                    backgroundColor: isPaymentOverdue
                                      ? "#fecaca"
                                      : "transparent",
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                  }}
                                >
                                  📅 Next payment:{" "}
                                  {formatDate(payment.nextPaymentDate)}
                                  {isPaymentOverdue && " ⚠️ OVERDUE"}
                                </Typography>
                              )}

                              {/* Payment Remarks */}
                              {payment.remarks && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    mt: 0.5,
                                    fontStyle: "italic",
                                    color: isOverdueRecord
                                      ? "#ef4444"
                                      : "text.secondary",
                                    backgroundColor: isOverdueRecord
                                      ? "#fecaca"
                                      : "transparent",
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                  }}
                                >
                                  {payment.remarks}
                                </Typography>
                              )}
                            </Box>

                            {/* Received By Info (only for actual payments) */}
                            {!isOverdueRecord && payment.receivedBy && (
                              <Box sx={{ textAlign: "right", minWidth: 120 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Received by
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ display: "block", fontWeight: 500 }}
                                >
                                  {userNames[payment.receivedBy] || "User"}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        quotation={selectedQuotation}
        lead={lead}
        userId={userId}
      />

      {/* Overdue Modal */}
      <OverdueModal
        open={overdueModalOpen}
        onClose={() => {
          setOverdueModalOpen(false);
          setSelectedQuotationForOverdue(null);
        }}
        quotation={selectedQuotationForOverdue}
        onConfirm={handleConfirmOverdue}
      />

      {/* Activity Timeline */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          overflow: "visible",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent sx={{ p: isSmallMobile ? 2 : isMobile ? 2.5 : 3 }}>
          {/* Activity Tracking Header */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: hasOverduePayments ? "error.main" : "primary.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "1.3rem",
              }}
            >
              Activity Tracking
            </Typography>
          </Box>

          {timelineData.events.length > 0 ? (
            <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
              {Object.entries(timelineData.groupedByMonth).map(
                ([monthYear, monthData]) => (
                  <Box key={monthYear} sx={{ mb: 4 }}>
                    {/* Month Header - Simplified */}
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                      <Typography
                        variant={isSmallMobile ? "body2" : "subtitle1"}
                        sx={{
                          fontWeight: 700,
                          color: "primary.main",
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(59, 130, 246, 0.1)"
                              : "rgba(59, 130, 246, 0.05)",
                          display: "inline-block",
                          px: 3,
                          py: 1,
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.primary.light}`,
                        }}
                      >
                        {monthYear}
                      </Typography>
                    </Box>

                    {/* Rest of your existing timeline code remains the same */}
                    {/* Dates for this month */}
                    {Object.entries(monthData).map(([dateKey, dateEvents]) => (
                      <Box key={dateKey} sx={{ mb: 3 }}>
                        {/* Date Header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            pl: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: theme.palette.primary.main,
                              mr: 2,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant={isSmallMobile ? "caption" : "body2"}
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {dateKey}
                          </Typography>
                        </Box>

                        {/* Events for this date */}
                        <Box
                          sx={{
                            pl: isSmallMobile ? 3 : 4,
                            position: "relative",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: "4px",
                              top: 0,
                              bottom: 0,
                              width: "2px",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.08)",
                              borderRadius: "1px",
                            },
                          }}
                        >
                          {dateEvents.map((event, eventIndex) => {
                            const iconColor = getActivityIconColor(
                              event.type,
                              event.title,
                              event.status
                            );

                            // Simplified activity type mapping with larger icons
                            const getActivityTypeConfig = (type, event) => {
                              const configs = {
                                status: {
                                  label: "Status",
                                  color: "primary",
                                  icon: (
                                    <Assignment sx={{ fontSize: "20px" }} />
                                  ),
                                },
                                quotation: {
                                  label: event.isRevised
                                    ? `Revision ${event.revisionNumber}`
                                    : "Quotation",
                                  color: "secondary",
                                  icon: (
                                    <Description sx={{ fontSize: "20px" }} />
                                  ),
                                },
                                payment: {
                                  label: "Payment",
                                  color: "success",
                                  icon: (
                                    <AttachMoney sx={{ fontSize: "20px" }} />
                                  ),
                                },
                                remark: {
                                  label: "Remark",
                                  color: "info",
                                  icon: (
                                    <CommentIcon sx={{ fontSize: "20px" }} />
                                  ),
                                },
                                meeting: {
                                  label: "Meeting",
                                  color: "warning",
                                  icon: <Videocam sx={{ fontSize: "20px" }} />,
                                },
                              };
                              return (
                                configs[type] || {
                                  label: "Activity",
                                  color: "default",
                                  icon: (
                                    <CommentIcon sx={{ fontSize: "20px" }} />
                                  ),
                                }
                              );
                            };

                            const typeConfig = getActivityTypeConfig(
                              event.type,
                              event
                            );

                            return (
                              <Box
                                key={event.id}
                                sx={{
                                  display: "flex",
                                  mb: 2,
                                  position: "relative",
                                  "&:last-child": { mb: 0 },
                                }}
                              >
                                {/* Premium Timeline Dot with Enhanced Highlight */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    mr: isSmallMobile ? 2 : 2.5,
                                    position: "relative",
                                    width: 48,
                                    minWidth: 48,
                                    zIndex: 2,
                                  }}
                                >
                                  {/* Premium Icon Container with Multiple Borders */}
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: "50%",
                                      backgroundColor: iconColor,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "white",
                                      zIndex: 3,
                                      position: "relative",
                                      // Main shadow
                                      boxShadow: `
                                  0 4px 16px ${iconColor}40,
                                  0 2px 8px ${iconColor}20,
                                  inset 0 1px 2px rgba(255,255,255,0.3)
                                `,
                                      border: `2px solid ${theme.palette.background.default}`,
                                      // Outer glow effect
                                      "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: "-4px",
                                        left: "-4px",
                                        right: "-4px",
                                        bottom: "-4px",
                                        borderRadius: "50%",
                                        border: `2px solid ${iconColor}30`,
                                        zIndex: -1,
                                        animation: "pulse 2s infinite",
                                      },
                                      // Inner highlight
                                      "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        top: "2px",
                                        left: "2px",
                                        right: "2px",
                                        bottom: "2px",
                                        borderRadius: "50%",
                                        background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%)`,
                                        zIndex: 1,
                                      },
                                    }}
                                  >
                                    {/* Icon with enhanced visibility */}
                                    <Box
                                      sx={{
                                        position: "relative",
                                        zIndex: 2,
                                        filter:
                                          "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
                                      }}
                                    >
                                      {typeConfig.icon}
                                    </Box>
                                  </Box>

                                  {/* Connector Line */}
                                  {eventIndex < dateEvents.length - 1 && (
                                    <Box
                                      sx={{
                                        width: "2px",
                                        height: "100%",
                                        backgroundColor:
                                          theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.1)"
                                            : "rgba(0,0,0,0.08)",
                                        position: "absolute",
                                        top: 44,
                                        bottom: -8,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        zIndex: 1,
                                      }}
                                    />
                                  )}
                                </Box>

                                {/* Event Content */}
                                <Box
                                  sx={{
                                    flex: 1,
                                    pb:
                                      eventIndex < dateEvents.length - 1
                                        ? 2
                                        : 0,
                                    minWidth: 0,
                                  }}
                                >
                                  {/* Activity Header */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Chip
                                      icon={React.cloneElement(
                                        typeConfig.icon,
                                        { sx: { fontSize: "16px" } }
                                      )}
                                      label={typeConfig.label}
                                      size="small"
                                      color={typeConfig.color}
                                      variant="filled"
                                      sx={{
                                        height: 28,
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        "& .MuiChip-icon": {
                                          color: "white !important",
                                        },
                                      }}
                                    />

                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontWeight: 500,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <ScheduleIcon sx={{ fontSize: 14 }} />
                                      {formatTime(event.date)}
                                    </Typography>

                                    {event.user && (
                                      <Typography
                                        variant="caption"
                                        color="primary.main"
                                        sx={{
                                          fontWeight: 600,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <PersonIcon sx={{ fontSize: 14 }} />
                                        {event.user}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Main Event Content */}
                                  <Box
                                    sx={{
                                      p: 2.5,
                                      backgroundColor:
                                        theme.palette.mode === "dark"
                                          ? "rgba(255,255,255,0.02)"
                                          : "rgba(0,0,0,0.02)",
                                      borderRadius: 2,
                                      border: `1px solid ${theme.palette.divider}`,
                                      boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                                    }}
                                  >
                                    <Typography
                                      variant={
                                        isSmallMobile ? "body2" : "body1"
                                      }
                                      sx={{
                                        fontWeight: 500,
                                        lineHeight: 1.5,
                                        color: "text.primary",
                                        mb: event.remark ? 1 : 0,
                                      }}
                                    >
                                      {event.title}
                                    </Typography>

                                    {/* Additional Remark */}
                                    {event.remark && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: "block",
                                          color: "text.secondary",
                                          lineHeight: 1.4,
                                          fontStyle: "italic",
                                          borderLeft: `3px solid ${iconColor}40`,
                                          pl: 1.5,
                                          ml: 0.5,
                                          mt: 1,
                                          py: 0.5,
                                        }}
                                      >
                                        {event.remark}
                                      </Typography>
                                    )}

                                    {/* Quotation PDF Section */}
                                    {event.type === "quotation" &&
                                      event.hasPDF && (
                                        <Box
                                          sx={{
                                            mt: 2,
                                            pt: 2,
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <Box>
                                              <Typography
                                                variant="caption"
                                                fontWeight="600"
                                                color="text.primary"
                                              >
                                                Quotation:{" "}
                                                {event.quotationNumber}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                              >
                                                Amount: ₹
                                                {event.finalAmount?.toLocaleString()}
                                              </Typography>
                                            </Box>
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              startIcon={
                                                pdfLoading ===
                                                  (event.details?._id ||
                                                    event.quotationData?._id) ? (
                                                  <CircularProgress size={16} />
                                                ) : (
                                                  <Preview
                                                    sx={{ fontSize: "18px" }}
                                                  />
                                                )
                                              }
                                              onClick={() =>
                                                handleViewQuotationPDF(event)
                                              }
                                              disabled={
                                                pdfLoading ===
                                                (event.details?._id ||
                                                  event.quotationData?._id)
                                              }
                                              sx={{
                                                borderColor:
                                                  theme.palette.secondary.main,
                                                color:
                                                  pdfLoading ===
                                                    (event.details?._id ||
                                                      event.quotationData?._id)
                                                    ? theme.palette.text
                                                      .disabled
                                                    : theme.palette.secondary
                                                      .main,
                                                "&:hover": {
                                                  backgroundColor:
                                                    theme.palette.secondary
                                                      .light,
                                                  borderColor:
                                                    theme.palette.secondary
                                                      .main,
                                                },
                                                minWidth: "120px", // Fixed width to prevent button resizing
                                              }}
                                            >
                                              {pdfLoading ===
                                                (event.details?._id ||
                                                  event.quotationData?._id)
                                                ? "Loading..."
                                                : "View PDF"}
                                            </Button>
                                          </Box>
                                        </Box>
                                      )}

                                    {/* Payment Details */}
                                    {event.type === "payment" && (
                                      <Box
                                        sx={{
                                          mt: 2,
                                          pt: 2,
                                          borderTop: `1px solid ${theme.palette.divider}`,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                          }}
                                        >
                                          <AttachMoney
                                            sx={{
                                              color: "success.main",
                                              fontSize: 18,
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: "success.main",
                                            }}
                                          >
                                            Payment Received
                                          </Typography>
                                        </Box>

                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 2,
                                            flexWrap: "wrap",
                                            alignItems: "center",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            fontWeight="600"
                                            color="success.main"
                                          >
                                            ₹{event.amount?.toLocaleString()}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {paymentTypeMap[event.paymentType]}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {
                                              paymentMethodMap[
                                              event.paymentMethod
                                              ]
                                            }
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CommentIcon
                sx={{
                  fontSize: 48,
                  color: "text.secondary",
                  mb: 2,
                  opacity: 0.5,
                }}
              />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No activities yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Activities will appear here as you update leads.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LeadDetails;
